import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { AuthorshipSchema, MessageSchema } from './primitives.js';
const c = initContract();
/**
 * Partner Groups — MD-01 field 4's list, one of MD-RULE-9's tenant-owned
 * vocabularies: "lists the customer creates, renames and switches off for
 * themselves". The prototype's four names are a seeded EXAMPLE the spec
 * explicitly refuses to freeze, so nothing here is seeded at all.
 *
 * What a group governs, per the field's own sentence ("a grouping used for
 * reporting and for default terms"):
 *   - reporting: the partner list takes a `group` filter;
 *   - default terms: `defaultPaymentTermId` fills a new member's MD-09 term
 *     when the create request picks a group and no term.
 *
 * Permission area: `crm` — the partners module's own (PART 16.2.2 row 5);
 * the group list is reached from the partner form's Group field.
 */
// ── The fixed sentences (MD-RULE-4's shape) ─────────────────────────────────
export const PARTNER_GROUP_MESSAGES = {
    nameRequired: 'Type a name for this group.',
    nameTaken: (name) => `There is already a group called ${name}.`,
    /** MD-RULE-4: name the count, offer the alternative. */
    inUse: (name, count) => `${name} has ${count} ${count === 1 ? 'partner' : 'partners'} in it and cannot be deleted. Switch it off instead.`,
    inactive: (name) => `${name} is switched off and cannot be picked.`,
};
/**
 * D46 — the partner-group family's CONVERTED parameterised refusals, structured.
 * `inUse`'s count is a genuine partner count (raw; noun-only partner/partners
 * plural); `nameTaken` carries a display name. Sentence parity pinned by
 * `refusal-census.spec.ts`.
 */
export const PARTNER_GROUP_REFUSALS = {
    inUse: (name, count) => ({
        message: PARTNER_GROUP_MESSAGES.inUse(name, count),
        key: 'partnerGroups.errInUse',
        params: { name, count },
    }),
    nameTaken: (name) => ({
        message: PARTNER_GROUP_MESSAGES.nameTaken(name),
        key: 'partnerGroups.errNameTaken',
        params: { name },
    }),
};
// ── Shapes ──────────────────────────────────────────────────────────────────
export const PartnerGroupSchema = z
    .object({
    id: z.uuid(),
    name: z.string(),
    /** The MD-09 term new members default to; null = no group default. */
    defaultPaymentTermId: z.uuid().nullable(),
    defaultPaymentTermName: z.string().nullable(),
    isActive: z.boolean(),
    /** The delete pre-check (MD-RULE-4's count). */
    partnersIn: z.number().int(),
    // MD-RULE-5's six fields, `createdAt` among them. rule-coverage:ignore
    ...AuthorshipSchema.shape,
})
    .strict();
export const CreatePartnerGroupRequestSchema = z
    .object({
    name: z.string().trim().min(1, PARTNER_GROUP_MESSAGES.nameRequired).max(60),
    defaultPaymentTermId: z.uuid().optional(),
})
    .strict();
export const UpdatePartnerGroupRequestSchema = z
    .object({
    name: z.string().trim().min(1, PARTNER_GROUP_MESSAGES.nameRequired).max(60).optional(),
    defaultPaymentTermId: z.uuid().nullable().optional(),
})
    .strict();
const groupParams = z.object({ groupId: z.uuid() }).strict();
export const partnerGroupsContract = c.router({
    list: {
        method: 'GET',
        path: '/api/partner-groups',
        query: z
            .object({
            q: z.string().optional(),
            includeInactive: z.enum(['true', 'false']).optional(),
        })
            .strict(),
        responses: {
            200: z.object({ groups: PartnerGroupSchema.array() }).strict(),
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'The group list (crm.view) — a tenant-owned vocabulary, unpaged like every vocabulary (PAGINATION.md c).',
    },
    get: {
        method: 'GET',
        path: '/api/partner-groups/:groupId',
        pathParams: groupParams,
        responses: {
            200: PartnerGroupSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'One group (crm.view).',
    },
    create: {
        method: 'POST',
        path: '/api/partner-groups',
        body: CreatePartnerGroupRequestSchema,
        responses: {
            201: PartnerGroupSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            409: MessageSchema,
        },
        summary: 'New group (crm.create). Duplicate names refused, case-insensitively.',
    },
    update: {
        method: 'PATCH',
        path: '/api/partner-groups/:groupId',
        pathParams: groupParams,
        body: UpdatePartnerGroupRequestSchema,
        responses: {
            200: PartnerGroupSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Edit a group (crm.edit). Renaming renames it on every partner at once — partners point at the id, never the text.',
    },
    deactivate: {
        method: 'POST',
        path: '/api/partner-groups/:groupId/deactivate',
        pathParams: groupParams,
        body: z.object({}).strict().optional(),
        responses: {
            200: PartnerGroupSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'Switch off (crm.edit) — not pickable on a NEW partner; members keep it (MD-RULE-2).',
    },
    reactivate: {
        method: 'POST',
        path: '/api/partner-groups/:groupId/reactivate',
        pathParams: groupParams,
        body: z.object({}).strict().optional(),
        responses: {
            200: PartnerGroupSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'Switch back on (crm.edit).',
    },
    remove: {
        method: 'DELETE',
        path: '/api/partner-groups/:groupId',
        pathParams: groupParams,
        body: c.noBody(),
        responses: {
            204: c.noBody(),
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Delete (crm.delete) — refused while any partner is in the group, with MD-RULE-4’s counted sentence; the RESTRICT FK refuses even a bug here.',
    },
}, { strictStatusCodes: true });
//# sourceMappingURL=partner-groups.js.map